/**
 * Offline Canvas Items Hook
 * 
 * Provides localStorage-based canvas item management for composer mode
 * Mimics the interface of useCanvasItems but without Supabase
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import type { CanvasItem } from './useCanvasItems';
import {
  loadOfflineCanvasItems,
  saveOfflineCanvasItem,
  removeOfflineCanvasItem,
  saveOfflineCanvasItems,
} from '@/utils/offline-canvas-storage';

export const useOfflineCanvasItems = () => {
  const [items, setItems] = useState<Map<string, CanvasItem>>(() => {
    // Load from localStorage on init
    return loadOfflineCanvasItems();
  });

  // Save to localStorage whenever items change
  useEffect(() => {
    saveOfflineCanvasItems(items);
  }, [items]);

  // Add new item
  const addItem = useCallback(async (item: CanvasItem) => {
    // Generate UUID if not provided
    const itemId = item.id || (globalThis as any).crypto?.randomUUID?.() || `item-${Date.now()}`;
    const newItem: CanvasItem = {
      ...item,
      id: itemId,
      createdAt: item.createdAt || new Date(),
    };

    // Add to local state
    setItems(prev => {
      const newItems = new Map(prev);
      newItems.set(newItem.id, newItem);
      return newItems;
    });

    // Save to localStorage (async for consistency with online version)
    await new Promise(resolve => setTimeout(resolve, 0));
    saveOfflineCanvasItem(newItem);
    
    console.log('✅ Offline item added:', newItem.id);
  }, []);

  // Update existing item
  const updateItem = useCallback(async (itemId: string, updates: Partial<CanvasItem>) => {
    setItems(prev => {
      const newItems = new Map(prev);
      const existingItem = newItems.get(itemId);
      
      if (existingItem) {
        const updatedItem = {
          ...existingItem,
          ...updates,
          updatedAt: new Date(),
        };
        newItems.set(itemId, updatedItem);
        
        // Save to localStorage
        saveOfflineCanvasItem(updatedItem);
        console.log('✅ Offline item updated:', itemId);
      }
      
      return newItems;
    });
  }, []);

  // Remove item
  const removeItem = useCallback(async (itemId: string) => {
    setItems(prev => {
      const newItems = new Map(prev);
      newItems.delete(itemId);
      return newItems;
    });

    // Remove from localStorage
    removeOfflineCanvasItem(itemId);
    console.log('✅ Offline item removed:', itemId);
  }, []);

  // No-op for offline mode (sections are always "loaded")
  const loadViewportSections = useCallback(async () => {
    // In offline mode, all items are always available
    return Promise.resolve();
  }, []);

  // Debug snapshot (compatible with online version)
  const getDebugSnapshot = useCallback(() => {
    return Array.from(items.values()).map(item => ({
      id: item.id,
      type: item.type,
      position: item.position,
      lastSavedAt: item.updatedAt || item.createdAt,
      lastSavedPosition: item.position,
      pending: false, // Never pending in offline mode
    }));
  }, [items]);

  // Debug meta (compatible with online version)
  const getDebugMeta = useCallback(() => {
    return {
      realtimeConnected: false, // Never connected in offline mode
      lastLoadAt: new Date(),
      lastLoadError: null,
      inFlightCount: 0,
      loadedCount: 1, // Everything is "loaded"
      lastRequestedKey: 'offline',
      totalItems: items.size,
    };
  }, [items]);

  const itemsArray = useMemo(() => Array.from(items.values()), [items]);

  return {
    items: itemsArray,
    addItem,
    updateItem,
    removeItem,
    loadViewportSections,
    getDebugSnapshot,
    getDebugMeta,
  };
};
